import {
  HIGHLIGHTED_SPAN_WRAPPER_ID,
  ALLOWED_TAGS,
} from './useSelectionData.constants';
import { SplitContentLengthTable } from './useSelectionData.type';

import { decodeHtmlEntities, isValidElement } from '@/shared/utils';

export const getSelectionRange = () => {
  const selection = window.getSelection();
  if (!selection || selection.type !== 'Range') return;

  return selection.getRangeAt(0);
};

export const getSelectedText = (range: Range) => {
  const rangeContent = range.cloneContents();
  const tempDiv = document.createElement('div');
  tempDiv.appendChild(rangeContent);
  return decodeHtmlEntities(tempDiv.innerHTML);
};

export const getValidTag = (
  node: Node,
  targetTag: string
): Element | undefined => {
  const parent = document.querySelector(targetTag);
  if (!parent) return;

  const getNearestValidElement = (element: Element | null): Element | null => {
    if (!element || element === parent) return null;
    if (isValidElement(element)) return element;
    return getNearestValidElement(element.parentElement);
  };

  const topLevelParent =
    node instanceof Element
      ? getNearestValidElement(node)
      : getNearestValidElement(node.parentElement);

  if (!topLevelParent) return;

  return topLevelParent;
};

export const checkIsInsideHighlightSpan = (node: Node): boolean => {
  try {
    let currentNode: Node | null = node;
    if (!currentNode) return false;

    while (!isValidElement(currentNode as Element)) {
      if (
        (currentNode as Element).id &&
        (currentNode as Element).id.includes(HIGHLIGHTED_SPAN_WRAPPER_ID)
      ) {
        return true;
      }

      currentNode = currentNode?.parentNode as Node;
    }
    return false;
  } catch (e) {
    return false;
  }
};

export const checkIsIncludingInlineCode = (
  node: Node,
  text: string
): boolean => {
  const currentNode: Node | null = node;
  if (!currentNode) return false;

  // 현재 노드가 code 태그인 경우
  if (
    currentNode.nodeType === Node.ELEMENT_NODE &&
    (currentNode as Element).tagName.toLowerCase() === 'code'
  ) {
    // 코드 태그 내용 추출
    const codePattern = /<code[^>]*>(.*?)<\/code>/g;
    const match = text.match(codePattern);
    const codeContents = match ? match[0].replace(/<[^>]*>/g, '') : '';
    const nodeContent = (currentNode as Element).textContent || '';
    // 추출된 코드 태그 내용과 현재 노드의 내용 비교
    return !codeContents.includes(nodeContent);
  }

  // 자식 노드들 순회
  const children = currentNode.childNodes;
  for (let i = 0; i < children.length; i++) {
    if (checkIsIncludingInlineCode(children[i], text)) {
      return true;
    }
  }

  return false;
};

export const getTargetRange = (range: Range) => {
  const container = range.commonAncestorContainer;
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;

  const targetRange: ChildNode[] = [];
  let startFound = false;

  if (container.nodeType === Node.TEXT_NODE) {
    return [container];
  }

  for (const child of Array.from(container.childNodes)) {
    if (
      !startFound &&
      (child === startContainer || child.contains(startContainer))
    ) {
      startFound = true;
    }

    if (startFound) {
      targetRange.push(child);
    }

    if (child === endContainer || child.contains(endContainer)) {
      break;
    }
  }

  return targetRange;
};

export const calculatePureTextStartIndex = (
  topLevelValidTag: Element,
  range: Range
): number => {
  const getTextLengthBeforeStartContainer = (
    rootNode: Node,
    startContainer: Node
  ): number => {
    let pureTextStartIndex = 0;

    const traverse = (node: Node) => {
      if (node === startContainer) {
        return true;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        pureTextStartIndex += node.textContent?.length || 0;
      }

      for (const child of node.childNodes) {
        if (traverse(child)) {
          return true;
        }
      }

      return false;
    };

    traverse(rootNode);
    return pureTextStartIndex;
  };

  const pureTextStartIndex =
    getTextLengthBeforeStartContainer(topLevelValidTag, range.startContainer) +
    range.startOffset;

  return pureTextStartIndex;
};

export const getSplitContentLengthTable = (currentElement: string) => {
  const tagPattern = new RegExp(
    // eslint-disable-next-line no-useless-escape
    `(<\/?(?:${ALLOWED_TAGS.join('|')})(?:\\s[^>]*|)>)`,
    'g'
  );

  const splitContentByTag = currentElement.split(tagPattern).filter(Boolean);

  return splitContentByTag.map((text): SplitContentLengthTable => {
    if (text.match(tagPattern)) {
      return { type: 'tag', length: text.length };
    } else {
      return { type: 'text', length: text.length };
    }
  });
};

export const getTagSums = (
  splitContentLengthTable: SplitContentLengthTable[],
  pureTextStartIndex: number,
  pureTextEndIndex: number
) => {
  return splitContentLengthTable.reduce(
    (acc, textInfo) => {
      const { textSum, startTagSum, endTagSum, reachedStart, reachedEnd } = acc;

      if (reachedEnd) return acc;

      const newTextSum =
        textInfo.type === 'text' ? textSum + textInfo.length : textSum;
      const newTagSum = textInfo.type === 'text' ? 0 : textInfo.length;

      const updatedStartTagSum = reachedStart
        ? startTagSum
        : newTextSum > pureTextStartIndex
          ? startTagSum
          : startTagSum + newTagSum;

      const updatedEndTagSum = reachedEnd
        ? endTagSum
        : newTextSum > pureTextEndIndex
          ? endTagSum
          : endTagSum + newTagSum;

      return {
        textSum: newTextSum,
        startTagSum: updatedStartTagSum,
        endTagSum: updatedEndTagSum,
        reachedStart: reachedStart || newTextSum > pureTextStartIndex,
        reachedEnd: reachedEnd || newTextSum > pureTextEndIndex,
      };
    },
    {
      textSum: 0,
      startTagSum: 0,
      endTagSum: 0,
      reachedStart: false,
      reachedEnd: false,
    }
  );
};
